/**
 * governance/systemStats.ts
 * Computes LIVE system statistics for the developer dashboard.
 *
 * PHASE 3 MIGRATION: Reduced dependency on legacy rulebookRegistry.
 * QUESTION_TYPE_REGISTRY import kept (shared reference, not legacy).
 *
 * ARCHITECTURE v4.0 — Phase 3, Step 3.4
 */

import {
  TAXONOMY,
  getAllModuleScopedSkills,
  ALL_QUESTIONS_CORE,
  QUESTIONS_BY_FAMILY,
} from '../content'
import { QUESTION_TYPE_REGISTRY } from './questionTypeDefinitions'
import {
  GENERIC_EXPLANATION_PATTERNS,
  GENERIC_DISTRACTOR_PATTERNS,
  isGenericText,
} from './utils'
import type { PipelineBatchResult } from './pipeline/issueContract'
import { FAMILY_SUPPORT_REGISTRY } from '../support'

// ── GovernanceSystemStats (was in governance/types.ts, now inline) ─────────────

export interface GovernanceSystemStats {
  totalFamilies: number
  totalSkills: number
  totalQuestions: number
  totalSeeds: number
  registeredRulebooks: number
  registeredQuestionTypes: number
  validatorVersion: string
  lastAuditTimestamp: number | null
  overallQualityScore: number | null
}

// ── Rule count ────────────────────────────────────────────────────────────────
// rulebookRegistry was deleted in Phase 3. Rule count is now 0.
const RULE_COUNT = 0

/**
 * Computes live system statistics.
 * @param pipelineReport Optional pre-computed pipeline batch result
 */
export function computeLiveSystemStats(
  pipelineReport?: PipelineBatchResult,
): GovernanceSystemStats {
  const totalFamilies = TAXONOMY.length
  const totalSkills = getAllModuleScopedSkills().length
  const totalQuestions = ALL_QUESTIONS_CORE.length
  const totalSeeds = totalQuestions
  const totalRules = RULE_COUNT
  const totalQuestionTypes = Object.keys(QUESTION_TYPE_REGISTRY).length

  const overallQualityScore = pipelineReport?.avgScore ?? null
  const lastAuditTimestamp = pipelineReport ? Date.now() : null

  return {
    totalFamilies,
    totalSkills,
    totalQuestions,
    totalSeeds,
    registeredRulebooks: totalRules,
    registeredQuestionTypes: totalQuestionTypes,
    validatorVersion: '4.2.0-pipeline',
    lastAuditTimestamp,
    overallQualityScore,
  }
}

/**
 * Computes detailed breakdown by module.
 */
export function computeModuleStats() {
  const stats: Record<
    string,
    { familyCount: number; questionCount: number; skillCount: number }
  > = {}

  TAXONOMY.forEach((family) => {
    const mod = family.module
    if (!stats[mod]) {
      stats[mod] = { familyCount: 0, questionCount: 0, skillCount: 0 }
    }
    stats[mod].familyCount++
  })

  ALL_QUESTIONS_CORE.forEach((q) => {
    const mod = q.module
    if (stats[mod]) {
      stats[mod].questionCount++
    }
  })

  const skills = getAllModuleScopedSkills()
  skills.forEach((skillKey) => {
    const [mod] = skillKey.split('::')
    if (stats[mod]) {
      stats[mod].skillCount++
    }
  })

  return stats
}

/**
 * Computes calibration-specific statistics for the difficulty calibration pipeline.
 * Tracks gold standard compliance, forbidden pattern prevalence, and field coverage.
 * Added in v5.8 of DIFFICULTY_CALIBRATION_PLAN.
 *
 * UPDATED v6.0: Gold standard now requires FULL SYSTEM INTEGRATION:
 * - ≥5 questions
 * - ALL have citoDifficulty
 * - ALL have solutionSteps
 * - NO forbidden patterns
 * - Has FAMILY_SUPPORT_REGISTRY entry with hints (NEW)
 * - Has supportAids in seed questions (NEW)
 *
 * IMPORTANT: Gold standard is CRITERIA-BASED, not exemplar-based.
 * ANY family meeting ALL criteria above qualifies as gold standard.
 * There is NO single "reference" or "exemplar" family — the system
 * must be validated against DIVERSE families across all modules and
 * question types. Do NOT use any single seed file (e.g. GET-KOMMA-AFRONDEN)
 * as "the" gold standard reference. Each module has different patterns
 * (interactive types, visual requirements, feedback structures) that
 * must all be independently validated.
 */
export function computeCalibrationStats() {
  const supportRegistry = FAMILY_SUPPORT_REGISTRY as Record<
    string,
    { hints?: unknown[] }
  >

  let totalQuestions = 0
  let withCitoDifficulty = 0
  let withSolutionSteps = 0
  let withSupportAids = 0
  let withStimulusAids = 0
  let withDistractorExplanations = 0
  let forbiddenExplanationPatterns = 0
  let forbiddenDistractorPatterns = 0
  let familiesBelow5 = 0
  let familiesAtGoldStandard = 0
  const familyStats: Array<{
    familyId: string
    questionCount: number
    hasCitoDifficulty: boolean
    hasSolutionSteps: boolean
    hasSupportRegistry: boolean
    hasSupportAids: boolean
    forbiddenPatternCount: number
    isGoldStandard: boolean
  }> = []

  for (const [familyId, questions] of QUESTIONS_BY_FAMILY.entries()) {
    let familyForbiddenCount = 0
    let familyHasCito = true
    let familyHasSolutionSteps = true
    let familyHasSupportAids = true

    if (questions.length < 5) {
      familiesBelow5++
    }

    for (const q of questions) {
      totalQuestions++

      // citoDifficulty
      if (q.citoDifficulty !== undefined && q.citoDifficulty !== null) {
        withCitoDifficulty++
      } else {
        familyHasCito = false
      }

      // solutionSteps
      const feedback = q.feedback
      if (
        feedback?.solutionSteps &&
        Array.isArray(feedback.solutionSteps) &&
        feedback.solutionSteps.length > 0
      ) {
        withSolutionSteps++
      } else {
        familyHasSolutionSteps = false
      }

      // supportAids
      if (
        q.supportAids &&
        Array.isArray(q.supportAids) &&
        q.supportAids.length > 0
      ) {
        withSupportAids++
      } else {
        familyHasSupportAids = false
      }

      // stimulusAids
      if (
        q.stimulusAids &&
        Array.isArray(q.stimulusAids) &&
        q.stimulusAids.length > 0
      ) {
        withStimulusAids++
      }

      // distractorExplanations
      if (
        feedback?.distractorExplanations &&
        Object.keys(feedback.distractorExplanations).length > 0
      ) {
        withDistractorExplanations++
      }

      // Check forbidden patterns in explanationText
      if (
        feedback?.explanationText &&
        typeof feedback.explanationText === 'string'
      ) {
        if (
          isGenericText(feedback.explanationText, GENERIC_EXPLANATION_PATTERNS)
        ) {
          forbiddenExplanationPatterns++
          familyForbiddenCount++
        }
      }

      // Check forbidden patterns in distractorExplanations
      if (feedback?.distractorExplanations) {
        for (const [, text] of Object.entries(
          feedback.distractorExplanations,
        )) {
          if (
            typeof text === 'string' &&
            isGenericText(text, GENERIC_DISTRACTOR_PATTERNS)
          ) {
            forbiddenDistractorPatterns++
            familyForbiddenCount++
          }
        }
      }
    }

    // Support registry check (NEW in v6.0)
    const supportEntry = supportRegistry[familyId]
    const hasSupportRegistry = !!(
      supportEntry &&
      supportEntry.hints &&
      Array.isArray(supportEntry.hints) &&
      supportEntry.hints.length > 0
    )

    // Gold standard v6.0: seed quality + system integration
    // Must have: ≥5 questions, citoDifficulty, solutionSteps, no forbidden patterns,
    // PLUS: support registry entry with hints AND supportAids in seeds
    const isGold =
      questions.length >= 5 &&
      familyHasCito &&
      familyHasSolutionSteps &&
      familyForbiddenCount === 0 &&
      hasSupportRegistry &&
      familyHasSupportAids
    if (isGold) {
      familiesAtGoldStandard++
    }

    familyStats.push({
      familyId,
      questionCount: questions.length,
      hasCitoDifficulty: familyHasCito,
      hasSolutionSteps: familyHasSolutionSteps,
      hasSupportRegistry,
      hasSupportAids: familyHasSupportAids,
      forbiddenPatternCount: familyForbiddenCount,
      isGoldStandard: isGold,
    })
  }

  return {
    totalQuestions,
    totalFamilies: QUESTIONS_BY_FAMILY.size,
    withCitoDifficulty,
    withSolutionSteps,
    withSupportAids,
    withStimulusAids,
    withDistractorExplanations,
    forbiddenExplanationPatterns,
    forbiddenDistractorPatterns,
    totalForbiddenPatterns:
      forbiddenExplanationPatterns + forbiddenDistractorPatterns,
    familiesBelow5,
    familiesAtGoldStandard,
    citoDifficultyCoveragePct:
      totalQuestions > 0
        ? Math.round((withCitoDifficulty / totalQuestions) * 100)
        : 0,
    solutionStepsCoveragePct:
      totalQuestions > 0
        ? Math.round((withSolutionSteps / totalQuestions) * 100)
        : 0,
    goldStandardPct:
      QUESTIONS_BY_FAMILY.size > 0
        ? Math.round((familiesAtGoldStandard / QUESTIONS_BY_FAMILY.size) * 100)
        : 0,
    familyStats,
  }
}
